// Сгъваемата група в менюто — „Операции" под „Табло". Заглавието е бутон със
// стрелка (aria-expanded), редовете са обикновени `SidebarNavItem` с тънка
// линия вляво, която ги закача за „Табло". СВИТА група пак показва реда на
// ОТВОРЕНАТА операция — иначе в „Доставка" менюто не казва къде си.
// Рейл (свито меню) — заглавието е само стрелка с tooltip, без отстъп.
import { useId } from "react";
import { Tooltip } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { navBadgeToneOf, visibleGroupItems, type InventoryNavGroup } from "../shell/inventoryNav";
import type { SidebarBadges } from "../shell/useSidebarBadges";
import { SidebarNavItem } from "./SidebarNavItem";

type Props = {
  group: InventoryNavGroup;
  open: boolean;
  onToggle: () => void;
  activeSlug: string;
  collapsed: boolean;
  badges: SidebarBadges;
  onNavigate: (slug: string) => void;
};

export const SidebarNavGroup = ({
  group,
  open,
  onToggle,
  activeSlug,
  collapsed,
  badges,
  onNavigate,
}: Props) => {
  const { t } = useTranslation();
  const listId = useId();
  const label = t(group.labelKey);
  const actionLabel = t(open ? "inventoryPage.dashGroupHide" : "inventoryPage.dashGroupShow", {
    name: label,
  });
  const items = visibleGroupItems(group, open, activeSlug);

  const chevron = (
    <RightOutlined
      className={`text-[9px] transition-transform duration-150 ${open ? "rotate-90" : ""}`}
    />
  );

  const toggle = collapsed ? (
    <Tooltip title={actionLabel} placement="right">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={listId}
        aria-label={actionLabel}
        className="mb-0.5 mt-1 flex w-full cursor-pointer items-center justify-center rounded-lg border-none bg-transparent py-1.5 text-muted outline-none hover:bg-shade-1 hover:text-ink focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)]"
      >
        {chevron}
      </button>
    </Tooltip>
  ) : (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={listId}
      title={actionLabel}
      className="mb-0.5 mt-2 flex w-full cursor-pointer items-center gap-2 rounded-lg border-none bg-transparent px-2.5 py-1.5 text-left text-[11px] font-semibold uppercase leading-[normal] tracking-[0.12em] text-muted outline-none hover:bg-shade-1 hover:text-ink focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)]"
    >
      {chevron}
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );

  return (
    <div>
      {toggle}
      <div
        id={listId}
        role="group"
        aria-label={label}
        className={collapsed ? "" : "ml-[17px] border-l border-shade-2 pl-1.5"}
      >
        {items.map((item) => (
          <SidebarNavItem
            key={item.slug}
            item={item}
            label={t(item.labelKey)}
            active={activeSlug === item.slug}
            collapsed={collapsed}
            badge={item.badge ? badges[item.badge] : undefined}
            badgeTone={item.badge ? navBadgeToneOf(item.badge, badges) : null}
            onClick={() => onNavigate(item.slug)}
          />
        ))}
      </div>
    </div>
  );
};

export default SidebarNavGroup;
